import React, { useState, useEffect } from "react";
import {
  Button,
  Space,
  message,
  Input,
  Drawer,
  Form,
  Select,
  Switch,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { collectionCategoryAPIWithErrorHandling, collectionCategoryHelpers } from "../../../api/collection/collectionCategory";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";
import { neutral, spacing } from "../../../theme/tokens";

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["description", "status"];

export default function CollectionCategoryTable() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [form] = Form.useForm();

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const fetchCategories = async (page = 1, pageSize = 15, search = "", status = "") => {
    setLoading(true);
    try {
      const params = {
        page,
        per_page: pageSize,
        search: search.trim(),
        status: status,
      };
      const response = await collectionCategoryAPIWithErrorHandling.getCategories(params);
      const { categories: data, pagination: paginationData } = collectionCategoryHelpers.extractCategoryList(response);

      setCategories(data);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách loại phí");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (paginationConfig) => {
    fetchCategories(paginationConfig.current, paginationConfig.pageSize, searchText, statusFilter);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchCategories(1, pagination.pageSize, value, statusFilter);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    fetchCategories(1, pagination.pageSize, searchText, value);
  };

  const handleReset = () => {
    setSearchText("");
    setStatusFilter("");
    fetchCategories(1, pagination.pageSize, "", "");
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await collectionCategoryAPIWithErrorHandling.toggleCategoryStatus(id, !currentStatus);
      message.success("Cập nhật trạng thái thành công");
      fetchCategories(pagination.current, pagination.pageSize, searchText, statusFilter);
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleDelete = async (record) => {
    const confirmed = await confirmDelete({
      content: `Bạn có chắc chắn muốn xóa loại phí "${record.name}"? Thao tác này không thể hoàn tác nếu loại phí chưa được gán cho đợt thu nào.`,
    });
    if (!confirmed) return;

    try {
      await collectionCategoryAPIWithErrorHandling.deleteCategory(record.id);
      message.success("Xóa loại phí thành công");
      fetchCategories(pagination.current, pagination.pageSize, searchText, statusFilter);
    } catch (error) {
      message.error(error.message);
    }
  };

  const showModal = (record = null) => {
    setSelectedRecord(record);
    if (record) {
      form.setFieldsValue({
        ...record,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ status: true });
    }
    setIsModalOpen(true);
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const data = {
        name: values.name,
        description: values.description || "",
        status: values.status ? 1 : 0,
      };

      if (selectedRecord) {
        await collectionCategoryAPIWithErrorHandling.updateCategory(selectedRecord.id, data);
        message.success("Cập nhật loại phí thành công");
      } else {
        await collectionCategoryAPIWithErrorHandling.createCategory(data);
        message.success("Thêm loại phí thành công");
      }

      setIsModalOpen(false);
      fetchCategories(pagination.current, pagination.pageSize, searchText, statusFilter);
    } catch (error) {
      if (error.name !== "ValidationError") {
        message.error(error.message || "Lỗi xử lý yêu cầu");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Định nghĩa cột =====
  const columnDefs = [
    {
      key: "name", label: "Tên loại phí", fixed: true,
      column: {
        title: "Tên loại phí", dataIndex: "name", key: "name",
        render: (text) => <span className="font-medium text-blue-600">{text}</span>,
      },
    },
    {
      key: "description", label: "Mô tả",
      column: {
        title: "Mô tả", dataIndex: "description", key: "description", ellipsis: true,
        render: (text) => text || <span className="text-gray-400 italic">Chưa có mô tả</span>,
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái", dataIndex: "status", key: "status", align: "center", width: 120,
        render: (status, record) => (
          <Space>
            <Switch
              size="small"
              checked={status}
              onChange={() => handleToggleStatus(record.id, status)}
            />
          </Space>
        ),
      },
    },
    actionColumnDef(
      (record) => [
        { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => showModal(record) },
        { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
      ],
      { width: 100 }
    ),
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Tìm kiếm loại phí..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
    {
      key: "status", label: "Trạng thái", span: { xs: 24, sm: 12, md: 6 },
      field: (
        <Select
          style={{ width: "100%" }}
          placeholder="Lọc trạng thái"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          allowClear
        >
          <Select.Option value="true">Đang hoạt động</Select.Option>
          <Select.Option value="false">Ngưng hoạt động</Select.Option>
        </Select>
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!(searchText || statusFilter);

  return (
    <BrandedPageLayout
      title="Danh mục đợt thu"
      subtitle="Quản lý danh mục loại phí dùng cho các đợt thu."
      breadcrumbItems={[{ title: "Danh mục đợt thu" }, { title: "Danh sách" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleReset}
        onReload={() => fetchCategories(pagination.current, pagination.pageSize, searchText, statusFilter)}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            Thêm loại phí
          </Button>
        }
      />

      <ListTableCard
        columns={columns}
        dataSource={categories}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có dữ liệu"
      />

      <Drawer
        title={
          <div className="flex items-center gap-2">
            {selectedRecord ? <EditOutlined className="text-blue-500" /> : <PlusOutlined className="text-green-500" />}
            <span>{selectedRecord ? "Chỉnh sửa loại phí" : "Thêm loại phí mới"}</span>
          </div>
        }
        placement="right"
        width="min(560px, 100vw)"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        destroyOnClose
        styles={{ body: { background: neutral[50], padding: spacing[6] } }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
            <Button onClick={() => setIsModalOpen(false)} disabled={submitting}>
              Hủy
            </Button>
            <Button type="primary" loading={submitting} onClick={handleModalSubmit}>
              {selectedRecord ? "Cập nhật" : "Thêm mới"}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" className="mt-4 pt-4 border-t">
          <Form.Item
            name="name"
            label="Tên loại phí"
            rules={[{ required: true, message: "Vui lòng nhập tên loại phí!" }]}
          >
            <Input placeholder="Ví dụ: Phí vệ sinh, Phí an ninh..." />
          </Form.Item>

          <Form.Item
            name="description"
            label="Mô tả"
          >
            <Input.TextArea rows={4} placeholder="Nhập mô tả về loại phí này..." />
          </Form.Item>

          <Form.Item
            name="status"
            label="Trạng thái"
            valuePropName="checked"
          >
            <Switch checkedChildren="Hoạt động" unCheckedChildren="Ngưng" />
          </Form.Item>
        </Form>
      </Drawer>
    </BrandedPageLayout>
  );
}
